
// PawsHealth: geminiService.ts - Assistant santé canin via Gemini API
import { GoogleGenAI, GenerateContentResponse, Type } from "@google/genai";
import { PetProfile, HealthEntry } from "../types";
import { MOODS, APPETITE_LEVELS, STOOL_SCORES, STOOL_COLORS } from "../constants";
import { config } from "../../shared/config/environment";

export const getHealthAdvice = async (profile: PetProfile, history: HealthEntry[], query: string, language: string = 'fr'): Promise<string> => {
  console.log("Anivista AI: Requesting advice for", profile.name);
  console.log("Anivista AI: Gemini API Key available:", !!config.geminiApiKey);
  console.log("Anivista AI: Gemini API Key length:", config.geminiApiKey?.length || 0);
  
  if (!config.geminiApiKey || config.geminiApiKey === 'your_gemini_api_key_here') {
    throw new Error("Clé API Gemini non configurée. Veuillez configurer VITE_GEMINI_API_KEY dans votre environnement.");
  }
  
  // Toujours initialiser GoogleGenAI avec l'objet contenant apiKey
  const ai = new GoogleGenAI({ apiKey: config.geminiApiKey });
  
  const historyContext = history.slice(0, 5).map(h => {
    const parts = [];
    parts.push(`- Date: ${new Date(h.date).toLocaleDateString()}`);
    if (h.weight) parts.push(`Weight: ${h.weight}kg`);
    if (h.mood) parts.push(`Mood: ${h.mood}`);
    if (h.appetite) parts.push(`Appetite: ${h.appetite}`);
    if (h.stoolScore) parts.push(`Stool Score (1-7): ${h.stoolScore}`);
    if (h.stoolColor) {
      const colorLabel = STOOL_COLORS.find(c => c.value === h.stoolColor)?.label || h.stoolColor;
      parts.push(`Stool Color: ${colorLabel}`);
    }
    if (h.symptoms && h.symptoms.length > 0) parts.push(`Symptoms: ${h.symptoms.join(', ')}`);
    return parts.join(', ');
  }).join('\n');

  const systemInstruction = `
    You are an expert veterinary health assistant for pets. Respond in the SAME LANGUAGE as the user's query.
    
    ANIMAL PROFILE:
    - Name: ${profile.name}
    - Species: ${profile.type} (IMPORTANT: adapt your advice to this species)
    - Breed: ${profile.breed}
    - Color: ${profile.color}
    - Birth Date: ${profile.birthDate}
    
    Recent history:
    ${historyContext}
    
    The stool scale ranges from 1 (very hard) to 7 (liquid), with 2 being ideal for dogs/cats. Stool colors can indicate problems.
    
    Provide useful, professional, and empathetic advice specifically for a ${profile.type}. 
    ALWAYS include a disclaimer: "I am an AI, not a veterinarian. Consult a professional for a diagnosis."
  `;

  try {
    const response: GenerateContentResponse = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: query,
      config: {
        systemInstruction: systemInstruction,
        temperature: 0.7,
      },
    });

    return response.text || "Je n'ai pas pu analyser la situation. Veuillez reformuler.";
  } catch (error) {
    console.error("Anivista AI Error:", error);
    return "Erreur de connexion avec l'assistant IA.";
  }
};

export const analyzeVoiceLog = async (transcribedText: string): Promise<Partial<HealthEntry> & { time?: string }> => {
  const ai = new GoogleGenAI({ apiKey: config.geminiApiKey });

  const validMoods = MOODS.map(m => m.value).join(', ');
  const validAppetites = APPETITE_LEVELS.map(a => a.value).join(', ');
  const validColors = STOOL_COLORS.map(c => c.value).join(', ');

  const systemInstruction = `
    Tu es un assistant qui extrait des données structurées d'une dictée vocale pour un carnet de santé animal.
    Date actuelle : ${new Date().toISOString()}.
    
    Extrais les données suivantes :
    1. Poids (nombre).
    2. Humeur (Valeurs exactes : ${validMoods}).
    3. Appétit (Valeurs exactes : ${validAppetites}).
    4. Score selles (1 à 7).
    5. Couleur selles (Valeurs exactes : ${validColors}).
    6. Notes (contexte).
    7. Heure (HH:MM).
    
    Retourne du JSON. Null si non mentionné.
  `;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: transcribedText,
      config: {
        systemInstruction: systemInstruction,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            weight: { type: Type.NUMBER, nullable: true },
            mood: { type: Type.STRING, nullable: true },
            appetite: { type: Type.STRING, nullable: true },
            stoolScore: { type: Type.INTEGER, nullable: true },
            stoolColor: { type: Type.STRING, nullable: true },
            notes: { type: Type.STRING, nullable: true },
            time: { type: Type.STRING, nullable: true }
          }
        }
      }
    });

    const text = response.text;
    if (!text) return {};
    return JSON.parse(text);
  } catch (error) {
    console.error("Error analyzing voice log:", error);
    return {};
  }
};
